import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Heart, Users, Shield, FileText, ArrowLeft, Loader2, Ban, CheckCircle,
  XCircle, Clock, Eye, Mail, Inbox, MessageSquare
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [profileFilter, setProfileFilter] = useState('');
  const [inboxFilter, setInboxFilter] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, profilesRes, inboxRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getUsers(),
        adminApi.getProfiles(),
        adminApi.getInbox().catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setProfiles(profilesRes.data.profiles);
      setMessages(inboxRes.data || []);
    } catch (err) {
      console.error('Admin load failed:', err);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleBlockUser = async (userId) => {
    try {
      const { data } = await adminApi.blockUser(userId);
      setUsers(users.map(u => u.id === userId ? { ...u, is_blocked: data.is_blocked } : u));
      toast.success(data.message);
    } catch { toast.error('Failed to update user'); }
  };

  const handleSetTier = async (userId, tier) => {
    try {
      await adminApi.setTier(userId, tier);
      setUsers(users.map(u => u.id === userId ? { ...u, tier } : u));
      toast.success(`Tier updated to ${tier}`);
    } catch { toast.error('Failed to update tier'); }
  };

  const handleSetProfileStatus = async (profileId, status) => {
    try {
      await adminApi.setProfileStatus(profileId, status);
      setProfiles(profiles.map(p => p.id === profileId ? { ...p, status } : p));
      toast.success(`Profile ${status}`);
    } catch { toast.error('Failed to update profile'); }
  };

  const loadProfilesByStatus = async (status) => {
    setProfileFilter(status);
    try {
      const { data } = await adminApi.getProfiles(status || null);
      setProfiles(data.profiles);
    } catch { toast.error('Failed to filter profiles'); }
  };

  const loadInboxByStatus = async (status) => {
    setInboxFilter(status);
    try {
      const { data } = await adminApi.getInbox(status || null);
      setMessages(data || []);
    } catch { toast.error('Failed to filter messages'); }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await adminApi.markRead(messageId);
      setMessages(messages.map(m => m.id === messageId ? { ...m, status: 'read' } : m));
      toast.success('Marked as read');
    } catch { toast.error('Failed to update'); }
  };

  const handleResolveMessage = async (messageId) => {
    try {
      await adminApi.resolveMessage(messageId);
      setMessages(messages.map(m => m.id === messageId ? { ...m, status: 'resolved' } : m));
      toast.success('Message resolved');
    } catch { toast.error('Failed to resolve'); }
  };

  const unreadCount = messages.filter(m => m.status === 'unread').length;

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-[#1a1a2e]/10 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1a1a2e] mb-2">Admin Access Required</h1>
          <Link to="/dashboard"><Button className="mt-4 rounded-full">Back to Dashboard</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Toaster position="top-right" richColors />

      <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#1a1a2e]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#1a1a2e]/50 hover:text-[#1a1a2e]" data-testid="admin-back">
            <ArrowLeft className="w-4 h-4" /><span className="text-sm">Dashboard</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#7c3aed]" />
            <span className="font-bold text-[#1a1a2e]">Admin Panel</span>
          </div>
          <div />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-[#7c3aed] animate-spin" /></div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full max-w-2xl grid-cols-4 bg-[#1a1a2e]/5 rounded-full p-1 mb-8">
              <TabsTrigger value="overview" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="users" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-users">Users</TabsTrigger>
              <TabsTrigger value="tributes" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-tributes">Tributes</TabsTrigger>
              <TabsTrigger value="inbox" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm relative" data-testid="tab-inbox">
                Inbox
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#db2777] text-white text-[10px] rounded-full flex items-center justify-center">{unreadCount}</span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Overview */}
            <TabsContent value="overview">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="admin-stats">
                {[
                  { label: 'Total Users', value: stats?.total_users, icon: Users, color: 'text-purple-500' },
                  { label: 'Total Tributes', value: stats?.total_profiles, icon: FileText, color: 'text-pink-500' },
                  { label: 'Public Tributes', value: stats?.public_profiles, icon: Eye, color: 'text-emerald-500' },
                  { label: 'Pending Review', value: stats?.pending_profiles, icon: Clock, color: 'text-amber-500' },
                  { label: 'Blocked Users', value: stats?.blocked_users, icon: Ban, color: 'text-red-500' },
                  { label: 'Total Extols', value: stats?.total_extols, icon: Heart, color: 'text-violet-500' },
                ].map(s => (
                  <Card key={s.label} className="border-[#1a1a2e]/5 bg-white shadow-sm">
                    <CardContent className="p-6">
                      <s.icon className={`w-8 h-8 ${s.color} mb-3`} strokeWidth={1.5} />
                      <p className="text-3xl font-bold text-[#1a1a2e]">{s.value ?? 0}</p>
                      <p className="text-sm text-[#1a1a2e]/50">{s.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Users */}
            <TabsContent value="users">
              <div className="space-y-3" data-testid="admin-users-list">
                {users.map(u => (
                  <Card key={u.id} className="border-[#1a1a2e]/5 bg-white">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-[#1a1a2e]">{u.name}</p>
                        <p className="text-sm text-[#1a1a2e]/50">{u.email}</p>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{u.role}</Badge>
                          <Badge className={`text-xs ${u.tier === 'free' ? 'bg-gray-100 text-gray-600' : u.tier === 'unlimited' ? 'bg-amber-100 text-amber-600' : 'bg-purple-100 text-purple-600'}`}>{u.tier || 'free'}</Badge>
                          {u.is_blocked && <Badge className="bg-red-100 text-red-600 text-xs">Blocked</Badge>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={u.tier || 'free'} onValueChange={(v) => handleSetTier(u.id, v)}>
                          <SelectTrigger className="w-32 text-xs rounded-full bg-white border-[#1a1a2e]/10"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="personal">Personal</SelectItem>
                            <SelectItem value="family">Family</SelectItem>
                            <SelectItem value="legacy">Legacy</SelectItem>
                            <SelectItem value="unlimited">Unlimited</SelectItem>
                          </SelectContent>
                        </Select>
                        {u.role !== 'admin' && (
                          <Button variant="outline" size="sm" onClick={() => handleBlockUser(u.id)}
                            className={`rounded-full text-xs ${u.is_blocked ? 'border-emerald-300 text-emerald-600' : 'border-red-300 text-red-600'}`}
                            data-testid={`block-user-${u.id}`}>
                            {u.is_blocked ? 'Unblock' : 'Block'}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Tributes */}
            <TabsContent value="tributes">
              <div className="flex gap-2 mb-6">
                {['', 'approved', 'pending', 'rejected'].map(s => (
                  <Button key={s} variant={profileFilter === s ? 'default' : 'outline'} size="sm"
                    className={`rounded-full text-xs ${profileFilter === s ? 'bg-[#7c3aed] text-white' : ''}`}
                    onClick={() => loadProfilesByStatus(s)} data-testid={`filter-${s || 'all'}`}>
                    {s || 'All'}
                  </Button>
                ))}
              </div>
              <div className="space-y-3" data-testid="admin-profiles-list">
                {profiles.map(p => (
                  <Card key={p.id} className="border-[#1a1a2e]/5 bg-white">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Link to={`/tribute/${p.id}`} className="font-semibold text-[#1a1a2e] hover:text-[#7c3aed]">{p.person_name}</Link>
                          <Badge className={`text-xs ${p.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : p.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                            {p.status}
                          </Badge>
                          <Badge variant="outline" className="text-xs">{p.privacy_setting}</Badge>
                        </div>
                        <p className="text-xs text-[#1a1a2e]/40 mt-1">
                          by {p.creator_name} ({p.creator_email}) · {p.profile_type}
                          {p.location_country && ` · ${p.location_country}`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {p.status !== 'approved' && (
                          <Button size="sm" onClick={() => handleSetProfileStatus(p.id, 'approved')}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-xs" data-testid={`approve-${p.id}`}>
                            <CheckCircle className="w-3 h-3 mr-1" />Approve
                          </Button>
                        )}
                        {p.status !== 'rejected' && (
                          <Button size="sm" variant="outline" onClick={() => handleSetProfileStatus(p.id, 'rejected')}
                            className="border-red-300 text-red-600 rounded-full text-xs" data-testid={`reject-${p.id}`}>
                            <XCircle className="w-3 h-3 mr-1" />Reject
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {profiles.length === 0 && <p className="text-center text-[#1a1a2e]/40 py-8">No tributes found</p>}
              </div>
            </TabsContent>

            {/* Inbox */}
            <TabsContent value="inbox">
              <div className="flex gap-2 mb-6">
                {['', 'unread', 'read', 'resolved'].map(s => (
                  <Button key={s} variant={inboxFilter === s ? 'default' : 'outline'} size="sm"
                    className={`rounded-full text-xs ${inboxFilter === s ? 'bg-[#7c3aed] text-white' : ''}`}
                    onClick={() => loadInboxByStatus(s)} data-testid={`inbox-filter-${s || 'all'}`}>
                    {s || 'All'}
                  </Button>
                ))}
              </div>
              <div className="space-y-3" data-testid="admin-inbox-list">
                {messages.map(m => {
                  const subjectLabels = { tribute_removal: 'Tribute Removal', general: 'General', feedback: 'Feedback', other: 'Other' };
                  return (
                    <Card key={m.id} className={`border-[#1a1a2e]/5 bg-white ${m.status === 'unread' ? 'border-l-4 border-l-[#7c3aed]' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <MessageSquare className={`w-4 h-4 ${m.status === 'unread' ? 'text-[#7c3aed]' : 'text-[#1a1a2e]/30'}`} />
                              <span className="font-semibold text-[#1a1a2e] text-sm">{m.name}</span>
                              <span className="text-xs text-[#1a1a2e]/40">{m.email}</span>
                              <Badge className={`text-xs ${m.status === 'unread' ? 'bg-purple-100 text-purple-600' : m.status === 'resolved' ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-600'}`}>
                                {m.status}
                              </Badge>
                            </div>
                            <Badge variant="outline" className="text-xs mb-2">{subjectLabels[m.subject] || m.subject}</Badge>
                            <p className="text-sm text-[#1a1a2e]/70 mt-1">{m.message}</p>
                            <p className="text-xs text-[#1a1a2e]/30 mt-2">{new Date(m.created_at).toLocaleString()}</p>
                          </div>
                          <div className="flex gap-2 ml-4 shrink-0">
                            {m.status === 'unread' && (
                              <Button size="sm" variant="outline" onClick={() => handleMarkRead(m.id)}
                                className="rounded-full text-xs border-[#1a1a2e]/10" data-testid={`read-${m.id}`}>
                                <Eye className="w-3 h-3 mr-1" />Read
                              </Button>
                            )}
                            {m.status !== 'resolved' && (
                              <Button size="sm" onClick={() => handleResolveMessage(m.id)}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-xs" data-testid={`resolve-${m.id}`}>
                                <CheckCircle className="w-3 h-3 mr-1" />Resolve
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                {messages.length === 0 && (
                  <div className="text-center py-12">
                    <Inbox className="w-12 h-12 text-[#1a1a2e]/10 mx-auto mb-3" />
                    <p className="text-[#1a1a2e]/40">No messages yet</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
